/**
 * Framed photo posters (`poster:photo`,
 * docs/superpowers/specs/2026-09-28-posters-design.md): a real photo hanging
 * in the air, for where seeing the real thing matters.
 *
 * Pure, like bounds.ts: the headset builds a poster from `posterLayout`, and
 * framing and the generator's checks measure the same one.
 */
export const POSTER_LIMITS = {
    perLesson: 4,
    widthMin: 0.4,
    widthMax: 3,
    aspectMin: 0.4,
    aspectMax: 2.5,
    /** Framing assumes this until the photo is known (width / height). */
    aspectDefault: 4 / 3,
    /** The frame's width around the photo and the mat strip, metres. */
    frame: 0.03,
    /** How deep the whole thing is, frame and backing board. */
    depth: 0.04,
    /** Seen more than this far off its front, a poster is seen edge-on or from behind. */
    sideAngle: 60,
    captionMax: 80,
    wantMax: 200,
    searchMax: 80,
    searchWords: 6,
    creditMax: 140,
};
/** The fields only a poster takes; the validator refuses them on anything else. */
export const POSTER_FIELDS = [
    'width',
    'facing',
    'caption',
    'want',
    'search',
    'media',
    'aspect',
    'credit',
];
/** What a poster never takes: it hangs still, sized by its width. */
export const POSTER_REFUSED = ['spin', 'radius', 'ring', 'tilt'];
/** What the platform fills in, never the model (posters check, filled lesson). */
export const POSTER_FILLED_FIELDS = ['media', 'aspect', 'credit'];
export function isPosterKind(kind) {
    return kind.startsWith('poster:');
}
/**
 * A poster's parts and sizes. `aspect` is the lesson's when it has one, else
 * `photoAspect` (the loaded photo's own), else 4:3.
 */
export function posterLayout(object, photoAspect) {
    const width = object.width ?? 1;
    const aspect = object.aspect ?? photoAspect ?? POSTER_LIMITS.aspectDefault;
    const height = width / aspect;
    const frame = POSTER_LIMITS.frame;
    // 6 cm on a 1.2 m poster: about 1.8° at the 1.9 m part A frames it at.
    const captionLetter = Math.max(0.035, 0.05 * width);
    const creditLetter = captionLetter * 0.6;
    const lines = (object.caption ? captionLetter * 1.4 : 0) + (object.credit ? creditLetter * 1.4 : 0);
    const strip = lines > 0 ? lines + captionLetter * 0.8 : 0;
    const outer = { width: width + 2 * frame, height: height + strip + 2 * frame };
    const photoY = outer.height / 2 - frame - height / 2;
    return {
        photo: { width, height },
        strip,
        frame,
        outer,
        photoY,
        stripY: photoY - height / 2 - strip / 2,
        captionLetter,
        creditLetter,
    };
}
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const isNumbers = (value) => Array.isArray(value) &&
    value.length === 3 &&
    value.every((n) => typeof n === 'number' && Number.isFinite(n));
/** Degrees about y that turn a poster at `from` to face `to`; 0 faces +z. */
function facingTowards(from, to) {
    const dx = (to[0] ?? 0) - from[0];
    const dz = (to[2] ?? 0) - from[2];
    if (Math.hypot(dx, dz) < 1e-6)
        return 0;
    return (Math.atan2(dx, dz) * 180) / Math.PI;
}
/**
 * Which way a poster faces, degrees about y (0 faces +z): as declared; else
 * towards where the pupil stands on the first step about it (its `focus` or
 * `view.look`) when that step fixes the viewpoint (`view.pos`); else towards
 * the scene's origin, where the pupil starts. Computed once for the lesson,
 * so a poster hangs still like a real print. A step that lets the engine
 * frame the poster then stands the pupil in front of it.
 */
export function posterFacing(lesson, id) {
    // Read defensively: the generator's checks reach here with raw answers.
    const doc = lesson;
    const objects = isRecord(doc['objects']) ? doc['objects'] : {};
    const object = objects[id];
    if (!isRecord(object) || !isNumbers(object['pos']))
        return 0;
    const pos = object['pos'];
    if (typeof object['facing'] === 'number')
        return object['facing'];
    const steps = Array.isArray(doc['steps']) ? doc['steps'] : [];
    for (const step of steps) {
        if (!isRecord(step))
            continue;
        const view = isRecord(step['view']) ? step['view'] : undefined;
        if (step['focus'] !== id && view?.['look'] !== id)
            continue;
        if (view && isNumbers(view['pos']))
            return facingTowards(pos, view['pos']);
        break;
    }
    return facingTowards(pos, [0, 0, 0]);
}
/** A poster's front, as a unit vector [x, z] on the floor. */
export function posterNormal(facing) {
    const radians = (facing * Math.PI) / 180;
    return [Math.sin(radians), Math.cos(radians)];
}
//# sourceMappingURL=posters.js.map